import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/setup/redux/RootReducer";
import * as area from "../redux/AreaRedux";
import * as jobTitle from "../redux/JobTitleRedux";
import { getAreas } from "../redux/AreaCRUD";
import { getJobTitles } from "../redux/JobTitleCRUD";
import { getAvailableUsers, uploadEmployeeImage } from "../redux/EmployeeCRUD";
import { getDepartments } from "@/modules/organizations/redux/DepartmentCRUD";
import { UserModel } from "../../users/models/UserModel";
import { getSalaryConfigGroups, SalaryConfigGroup } from "@/modules/settings/redux/SettingsCRUD";


import JobPositionSection from "./job-positions/JobPositionSection";
import AdditionalInfoSection from "./addition/AdditionalInfoSection";
import AccountInfo from "./account/AccountInfo";
import AccountSalary from "./account/AccountSalary";
import BasicInfo from "./account/BasicInfo";
import AppForm from '@/__core/components/AppForm';
import { MessageSnackbar } from "@/__core/helpers/components";
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';
import { getCurrentCompanyId, getSelectedCompany } from '@/modules/auth/helpers/CompanyStorage';
import * as auth from '@/modules/auth/redux/AuthRedux';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { getUserRolesByUser } from '@/modules/users/redux/PermissionCRUD';
import { updateUser } from '@/modules/users/redux/UserCRUD';
import { NewEmployeeProps, UserSelectionType } from "../models/Employee";
import { resolveEmployeeStatusId } from "../utils/employeeStatus";

type EmployeeStatusOption = {
  id: string;
  name: string;
  color?: string;
  is_default?: boolean;
  sort_order?: number;
  group?: {
    id?: string;
    name?: string;
    sort_order?: number;
  };
};

const normalizeMissions = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object') return String(item.code || '').trim();
        return String(item || '').trim();
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeDashboards = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object') return String(item.code || '').trim();
        return String(item || '').trim();
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeSalaryConfigGroupId = (value: any): string => {
  if (!value) return "";
  if (typeof value === "object") return String(value.id || "").trim();
  return String(value).trim();
};

const normalizeSalaryConfigGroupIds = (value: any): string[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map(normalizeSalaryConfigGroupId)
    .filter(Boolean);
};

const NewEmployee = React.memo(function NewEmployee({
  onSave,
  onUpdate,
  onCancel,
  error = null,
  successMessage: propSuccessMessage = null,
  onClearError,
  onClearSuccess,
  onUnsavedChanges,
  isActive = true,
  tabId,
  employee,
  isEditMode: initialEditMode = false,
  initialFormData: propInitialFormData,
  activeCompanyFilter,
  canCreate: canCreateProp = false,
  canUpdate: canUpdateProp = false,
}: NewEmployeeProps) {
  const dispatch = useDispatch();
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();

  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  const canCreate = canCreateProp;
  const canUpdate = canUpdateProp;

  // Check if user has global role
  const hasGlobalRole = selectedCompany?.roles?.some((role: any) =>
    role.role_name === 'global' ||
    role.role_level === 'global' ||
    role.role_name === 'global_admin'
  ) ?? false;

  // Force load company from storage if not present
  useEffect(() => {
    if (!selectedCompany) {
      const storedCompany = getSelectedCompany();
      if (storedCompany) {
        dispatch(auth.actions.setSelectedCompany(storedCompany));
      }
    }
  }, [selectedCompany, dispatch]);


  // Redux selectors for companies and departments
  const companies = useSelector((state: RootState) => state.company.companies);
  const departments = useSelector(
    (state: RootState) => state.department.departments
  );

  const safeCompanies = useMemo(
    () => (Array.isArray(companies) ? companies : []),
    [companies]
  );
  const safeDepartments = useMemo(
    () => (Array.isArray(departments) ? departments : []),
    [departments]
  );

  const [apiDepartments, setApiDepartments] = useState<any[]>([]);

  // Form state
  const [userSelectionType, setUserSelectionType] = useState<
    UserSelectionType
  >("new");
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);
  const [formData, setFormData] = useState(() => {
    // Use propInitialFormData if available, otherwise use default values
    if (propInitialFormData) {
      return {
        name: propInitialFormData.name || "",
        nickname: propInitialFormData.nickname || "",
        code: propInitialFormData.code || "NV-{}",
        status: propInitialFormData.status || "active",
        job_title: propInitialFormData.job_title || propInitialFormData.job_title_id || "",
        area: propInitialFormData.area || "",
        office: propInitialFormData.office || "",
        position: propInitialFormData.position || "",
        company: propInitialFormData.company || "",
        department: propInitialFormData.department || "",
        date_of_birth: propInitialFormData.date_of_birth || "",
        gender: propInitialFormData.gender || "",
        phone: propInitialFormData.phone || "",
        start_date: propInitialFormData.start_date || "",
        resign_date: propInitialFormData.resign_date || "",
        bank_account_name: propInitialFormData.bank_account_name || "",
        bank_account_number: propInitialFormData.bank_account_number || "",
        bank_name: propInitialFormData.bank_name || "",
        notes: propInitialFormData.notes || "",
        relative_info: propInitialFormData.relative_info || "",
        image1: propInitialFormData.image1 || "",
        image2: propInitialFormData.image2 || "",
        address: propInitialFormData.address || {
          address_line: "",
          province: "",
          ward: "",
          free_address: "",
        },
        email: propInitialFormData.email || "",
        password: propInitialFormData.password || "",
        confirmPassword: propInitialFormData.confirmPassword || "",
        fullName: propInitialFormData.fullName || "",
        roles: Array.isArray(propInitialFormData.roles)
          ? propInitialFormData.roles.map((r: any) => String(r)).sort()
          : [],
        attendance_type: propInitialFormData.attendance_type || 'attendance',
        missions: normalizeMissions(propInitialFormData.missions),
        dashboards: normalizeDashboards(propInitialFormData.dashboards),
        salary_config_group: normalizeSalaryConfigGroupId(
          propInitialFormData.salary_config_group || propInitialFormData.primary_salary_config_group,
        ),
        salary_config_groups_secondary: normalizeSalaryConfigGroupIds(
          Array.isArray(propInitialFormData.salary_config_groups_secondary)
            ? propInitialFormData.salary_config_groups_secondary
            : propInitialFormData.secondary_salary_config_groups,
        ),
      };
    }

    return {
      name: "",
      nickname: "",
      code: "NV-{}",
      status: "active",
      job_title: "",
      area: "",
      office: "",
      position: "",
      company: "",
      department: "",
      date_of_birth: "",
      gender: "",
      phone: "",
      start_date: "",
      resign_date: "",
      bank_account_name: "",
      bank_account_number: "",
      bank_name: "",
      notes: "",
      relative_info: "",
      image1: "",
      image2: "",
      address: {
        address_line: "",
        province: "",
        ward: "",
        free_address: "",
      },
      email: "",
      password: "",
      confirmPassword: "",
      fullName: "",
      roles: [],
      attendance_type: 'attendance' as const,
      missions: [] as string[],
      dashboards: [] as string[],
      salary_config_group: "",
      salary_config_groups_secondary: [] as string[],
    };
  });

  const [salaryConfigGroups, setSalaryConfigGroups] = useState<SalaryConfigGroup[]>([]);
  const [employeeStatuses, setEmployeeStatuses] = useState<EmployeeStatusOption[]>([
    { id: "active", name: "Hoạt động", color: "#10b981", is_default: true, sort_order: 1 },
    { id: "inactive", name: "Không hoạt động", color: "#ef4444", is_default: false, sort_order: 2 },
    { id: "pending", name: "Chờ duyệt", color: "#f59e0b", is_default: false, sort_order: 3 },
  ]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditMode] = useState(initialEditMode);
  const [createdEmployee, setCreatedEmployee] = useState<any>(null);
  const [initialFormData, setInitialFormData] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resolvePreferredCompanyId = React.useCallback(() => {
    const normalizedActiveCompany = String(activeCompanyFilter || '').trim();
    if (
      normalizedActiveCompany &&
      normalizedActiveCompany !== 'all' &&
      normalizedActiveCompany !== 'global'
    ) {
      return normalizedActiveCompany;
    }

    const selectedCompanyId = String(selectedCompany?.company_id || '').trim();
    if (selectedCompanyId) {
      return selectedCompanyId;
    }

    const storageCompanyId = String(getCurrentCompanyId() || '').trim();
    return storageCompanyId;
  }, [activeCompanyFilter, selectedCompany?.company_id]);

  // Determine if user can edit (has create or update permission)
  const canEdit = isEditMode ? canUpdate : canCreate;

  // Track loaded employee to avoid re-loading on tab switch
  const employeeLoadedRef = useRef<string | null>(null);
  const initialFormDataSetRef = useRef<string | null>(null);
  // Store roles from formData before loading employee data (for newly created employees)
  const rolesBeforeLoadRef = useRef<string[]>([]);
  const newUserDraftRef = useRef({
    fullName: "",
    email: "",
    nickname: "",
  });


  // Auto-set company from active filter / selected company for create mode.
  // Company field is disabled in UI, so this keeps payload valid without manual input.
  useEffect(() => {
    if (isEditMode) {
      return;
    }

    const preferredCompanyId = resolvePreferredCompanyId();
    if (!preferredCompanyId || formData.company === preferredCompanyId) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      company: preferredCompanyId,
      department: prev.company !== preferredCompanyId ? '' : prev.department,
    }));
  }, [isEditMode, formData.company, resolvePreferredCompanyId]);


  useEffect(() => {
    if (!isActive) return;

    let isCancelled = false;

    getDepartments()
      .then((response) => {
        if (isCancelled) return;

        const payload = response.data as any;
        const normalizedDepartments = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.results) ? payload.results : []);

        setApiDepartments(normalizedDepartments);
      })
      .catch(() => {
        if (!isCancelled) {
          setApiDepartments([]);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isActive]);

  const departmentsSource = useMemo(() => {
    if (safeDepartments.length > 0) {
      return safeDepartments;
    }
    return apiDepartments;
  }, [safeDepartments, apiDepartments]);

  // Filter departments based on selected company
  const filteredDepartments = useMemo(() => {
    if (!formData.company) return [];
    return departmentsSource.filter((dept: any) => dept?.is_active !== false);
  }, [departmentsSource, formData.company]);

  const roleSelectorCompanyIds = useMemo(() => {
    const normalizedSelectedCompany = String(selectedCompany?.company_id || '').trim();
    const normalizedActiveCompany = String(activeCompanyFilter || '').trim();
    const fallbackCompanyId =
      normalizedActiveCompany &&
      normalizedActiveCompany !== 'all' &&
      normalizedActiveCompany !== 'global'
        ? normalizedActiveCompany
        : normalizedSelectedCompany;

    if (!hasGlobalRole) {
      return fallbackCompanyId ? [fallbackCompanyId] : undefined;
    }

    const allCompanyIds = safeCompanies
      .map((company: any) => String(company?.id || company?.company_id || '').trim())
      .filter(Boolean);

    const uniqueCompanyIds = Array.from(new Set(allCompanyIds));
    if (uniqueCompanyIds.length > 0) {
      return uniqueCompanyIds;
    }

    return fallbackCompanyId ? [fallbackCompanyId] : undefined;
  }, [hasGlobalRole, activeCompanyFilter, safeCompanies, selectedCompany?.company_id]);

  const scopedJobPositionCompanies = useMemo(() => {
    const normalizedActiveCompany = String(activeCompanyFilter || '').trim();
    if (
      normalizedActiveCompany &&
      normalizedActiveCompany !== 'all' &&
      normalizedActiveCompany !== 'global'
    ) {
      return safeCompanies.filter(
        (company: any) => String(company.id) === normalizedActiveCompany
      );
    }

    return safeCompanies;
  }, [activeCompanyFilter, safeCompanies]);

  useEffect(() => {
    if (!canEdit) {
      return;
    }

    // In edit mode, keep the employee's mapped company/department values.
    // Forcing company by activeCompanyFilter here can clear mapped department.
    if (isEditMode) {
      return;
    }

    const normalizedActiveCompany = String(activeCompanyFilter || '').trim();
    if (
      normalizedActiveCompany &&
      normalizedActiveCompany !== 'all' &&
      normalizedActiveCompany !== 'global' &&
      formData.company !== normalizedActiveCompany
    ) {
      setFormData((prev) => ({
        ...prev,
        company: normalizedActiveCompany,
        department: '',
      }));
    }
  }, [activeCompanyFilter, canEdit, formData.company, isEditMode]);

  const normalizeAvailableUser = (rawUser: any): UserModel => {
    const fullNameFromSingleField = String(rawUser?.full_name || rawUser?.name || '').trim();
    const splitName = fullNameFromSingleField ? fullNameFromSingleField.split(/\s+/) : [];

    const firstName = String(rawUser?.first_name || splitName[0] || '').trim();
    const lastName = String(rawUser?.last_name || splitName.slice(1).join(' ') || '').trim();
    const email = String(rawUser?.email || rawUser?.user_email || '').trim();
    const username = String(rawUser?.username || rawUser?.user_name || email.split('@')[0] || '').trim();

    return {
      ...rawUser,
      id: String(rawUser?.id || ''),
      first_name: firstName,
      last_name: lastName,
      email,
      username,
      status: (rawUser?.status || 'active') as 'active' | 'inactive' | 'pending',
      role: String(rawUser?.role || ''),
      role_display_name: String(rawUser?.role_display_name || ''),
      groups: Array.isArray(rawUser?.groups) ? rawUser.groups : [],
      is_active: typeof rawUser?.is_active === 'boolean' ? rawUser.is_active : true,
      date_joined: String(rawUser?.date_joined || ''),
      updated_at: String(rawUser?.updated_at || ''),
    };
  };

  const getUserDisplayName = (user: Partial<UserModel> & Record<string, any>) => {
    const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    if (fullName) {
      return fullName;
    }

    const fallbackName = String(user?.full_name || user?.name || '').trim();
    if (fallbackName) {
      return fallbackName;
    }

    const username = String(user?.username || user?.user_name || '').trim();
    if (username) {
      return username;
    }

    return String(user?.email || user?.user_email || '').trim() || 'Unknown User';
  };

  const effectiveAvailableUsersCompanyFilter = useMemo(() => {
    const normalizedActiveCompany = String(activeCompanyFilter || '').trim();
    if (
      normalizedActiveCompany &&
      normalizedActiveCompany !== 'all' &&
      normalizedActiveCompany !== 'global'
    ) {
      return normalizedActiveCompany;
    }

    const normalizedFormCompany = String(formData.company || '').trim();
    if (normalizedFormCompany) {
      return normalizedFormCompany;
    }

    const normalizedSelectedCompany = String(selectedCompany?.company_id || '').trim();
    return normalizedSelectedCompany || undefined;
  }, [activeCompanyFilter, formData.company, selectedCompany?.company_id]);

  const extractUserCompanyIds = (user: any): string[] => {
    const companyIds = new Set<string>();

    const directCompany = user?.company;
    if (typeof directCompany === 'string' || typeof directCompany === 'number') {
      const value = String(directCompany).trim();
      if (value) {
        companyIds.add(value);
      }
    }

    const directCompanyId = String(user?.company?.id || user?.company?.company_id || user?.company_id || user?.companyId || '').trim();
    if (directCompanyId) {
      companyIds.add(directCompanyId);
    }

    const companies = Array.isArray(user?.companies) ? user.companies : [];
    companies.forEach((company: any) => {
      const companyId = String(company?.company_id || company?.id || company?.company || '').trim();
      if (companyId) {
        companyIds.add(companyId);
      }
    });

    return Array.from(companyIds);
  };

  const scopeAvailableUsers = useCallback(
    (users: UserModel[]) => {
      if (!effectiveAvailableUsersCompanyFilter) {
        return users;
      }

      const usersWithCompanyMetadata = users.filter((user: any) => {
        return extractUserCompanyIds(user).length > 0;
      });

      if (usersWithCompanyMetadata.length === 0) {
        return users;
      }

      return users.filter((user: any) => {
        if (isEditMode && employee?.user?.id && String(user?.id) === String(employee.user.id)) {
          return true;
        }

        const userCompanyIds = extractUserCompanyIds(user);
        return userCompanyIds.includes(effectiveAvailableUsersCompanyFilter);
      });
    },
    [effectiveAvailableUsersCompanyFilter, employee?.user?.id, isEditMode]
  );

  const fetchAvailableUsers = useCallback(
    async (search: string) => {
      const response = await getAvailableUsers({
        company: effectiveAvailableUsersCompanyFilter,
        search: search.trim() || undefined,
      });

      const rawResponse = response.data;
      const availableUsersData = Array.isArray(rawResponse)
        ? rawResponse
        : (Array.isArray((rawResponse as any)?.results) ? (rawResponse as any).results : []);

      let normalizedUsers: UserModel[] = availableUsersData
        .map((user: any) => normalizeAvailableUser(user))
        .filter((user: UserModel) => Boolean(user?.id));

      if (isEditMode && employee?.user) {
        const currentUser: UserModel = {
          id: employee.user.id,
          email: employee.user.email,
          first_name: employee.user.first_name,
          last_name: employee.user.last_name,
          username: employee.user.username,
          is_active: true,
          date_joined: "",
          role: "",
          role_display_name: "",
          status: "active",
          groups: [],
          updated_at: "",
        };

        if (!normalizedUsers.some((user: UserModel) => user.id === currentUser.id)) {
          normalizedUsers = [...normalizedUsers, currentUser];
        }
      }

      return scopeAvailableUsers(normalizedUsers);
    },
    [
      effectiveAvailableUsersCompanyFilter,
      employee?.user,
      isEditMode,
      scopeAvailableUsers,
    ]
  );

  // Check if form data has changed
  const hasFormDataChanged = (() => {
    // Check permissions - if user can't edit, disable form
    if (!canEdit) return false;

    // Always disabled when submitting (but not when just loading data)
    if (isSubmitting) return false;

    // For create mode, check if form has required data
    if (!isEditMode) {
      const requiresEmail = userSelectionType !== "no-user";
      const hasRequiredEmail = !requiresEmail || !!formData.email.trim();
      return !!formData.fullName.trim() && hasRequiredEmail && !!formData.company;
    }

    // For edit mode, only allow saving if we have initial data and there are changes
    if (!initialFormData) {
      return false;
    }

    // Helper function to compare address objects (like useOrderForm)
    const compareAddressObj = (addr1: any, addr2: any): boolean => {
      // Both null/undefined - equal
      if ((addr1 == null || addr1 === '') && (addr2 == null || addr2 === '')) return true;
      
      // One is null/undefined, other is not - not equal
      if ((addr1 == null || addr1 === '') !== (addr2 == null || addr2 === '')) {
        return false;
      }
      
      // Object comparison
      if (typeof addr1 === 'object' && typeof addr2 === 'object' && addr1 !== null && addr2 !== null) {
        const isEqual = (
          (addr1.address_line || '') === (addr2.address_line || '') &&
          (addr1.province || '') === (addr2.province || '') &&
          (addr1.ward || '') === (addr2.ward || '') &&
          (addr1.free_address || '') === (addr2.free_address || '')
        );
        
        return isEqual;
      }
      
      // Type mismatch
      return false;
    };

    // Helper function to compare arrays (especially for roles)
    const compareArrays = (arr1: any[], arr2: any[]): boolean => {
      if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
        return arr1 === arr2;
      }
      if (arr1.length !== arr2.length) return false;
      
      // Normalize arrays: convert to strings and sort for comparison
      const normalized1 = arr1.map(item => String(item)).sort();
      const normalized2 = arr2.map(item => String(item)).sort();
      
      return normalized1.every((item, index) => item === normalized2[index]);
    };

    // Compare current formData with initialFormData
    const currentData = {
      name: formData.name,
      nickname: formData.nickname,
      code: formData.code,
      status: resolveEmployeeStatusId(formData.status),
      job_title: formData.job_title,
      area: formData.area,
      company: formData.company,
      department: formData.department,
      date_of_birth: formData.date_of_birth,
      gender: formData.gender,
      phone: formData.phone,
      start_date: formData.start_date,
      resign_date: formData.resign_date,
      bank_account_name: formData.bank_account_name,
      bank_account_number: formData.bank_account_number,
      bank_name: formData.bank_name,
      notes: formData.notes,
      relative_info: formData.relative_info,
      image1: formData.image1,
      image2: formData.image2,
      address: formData.address,
      email: formData.email,
      fullName: formData.fullName,
      roles: formData.roles,
      attendance_type: formData.attendance_type,
      missions: normalizeMissions(formData.missions),
      dashboards: normalizeDashboards(formData.dashboards),
      salary_config_group: formData.salary_config_group,
      salary_config_groups_secondary: formData.salary_config_groups_secondary,
    };

    // Simple field-by-field comparison
    const rolesEqual = compareArrays(currentData.roles, initialFormData.roles);
    const addressEqual = compareAddressObj(currentData.address, initialFormData.address);
    const secondaryEqual = compareArrays(
      currentData.salary_config_groups_secondary || [],
      initialFormData.salary_config_groups_secondary || [],
    );
    
    const hasChanges = (
      currentData.name !== initialFormData.name ||
      currentData.nickname !== initialFormData.nickname ||
      currentData.code !== initialFormData.code ||
      currentData.status !== initialFormData.status ||
      currentData.job_title !== initialFormData.job_title ||
      currentData.area !== initialFormData.area ||
      currentData.company !== initialFormData.company ||
      currentData.department !== initialFormData.department ||
      currentData.date_of_birth !== initialFormData.date_of_birth ||
      currentData.gender !== initialFormData.gender ||
      currentData.phone !== initialFormData.phone ||
      currentData.start_date !== initialFormData.start_date ||
      currentData.resign_date !== initialFormData.resign_date ||
      currentData.bank_account_name !== initialFormData.bank_account_name ||
      currentData.bank_account_number !== initialFormData.bank_account_number ||
      currentData.bank_name !== initialFormData.bank_name ||
      currentData.notes !== initialFormData.notes ||
      currentData.relative_info !== initialFormData.relative_info ||
      currentData.image1 !== initialFormData.image1 ||
      currentData.image2 !== initialFormData.image2 ||
      currentData.email !== initialFormData.email ||
      currentData.fullName !== initialFormData.fullName ||
      !addressEqual ||
      !rolesEqual ||
      currentData.attendance_type !== initialFormData.attendance_type ||
      !compareArrays(currentData.missions, normalizeMissions(initialFormData.missions)) ||
      !compareArrays(currentData.dashboards, normalizeDashboards(initialFormData.dashboards)) ||
      currentData.salary_config_group !== (initialFormData.salary_config_group || "") ||
      !secondaryEqual
    );


    return hasChanges;
  })();

  // Notify parent component about unsaved changes
  useEffect(() => {
    if (onUnsavedChanges && isActive) {
      onUnsavedChanges(hasFormDataChanged, tabId);
    }
  }, [hasFormDataChanged, onUnsavedChanges, isActive, tabId, isEditMode]);


  // Load areas and job titles (combined into single loading)
  useEffect(() => {
    // Only load if tab is active
    if (!isActive) return;
    
    if (!(window as any).__employeeDataFetched) {
      (window as any).__employeeDataFetched = true;
      startGlobalLoading('Đang tải dữ liệu...', 'employee-data-load');
      dispatch(area.actions.requestAreas());
      dispatch(jobTitle.actions.requestJobTitles());
      Promise.all([
        getAreas(),
        getJobTitles()
      ])
        .then(([areasResponse, jobTitlesResponse]) => {
          const areasData = areasResponse.data.results || areasResponse.data || [];
          const jobTitlesData = jobTitlesResponse.data.results || jobTitlesResponse.data || [];
          
          dispatch(area.actions.fulfillAreas(areasData));
          dispatch(jobTitle.actions.fulfillJobTitles(jobTitlesData));
        })
        .catch((error) => {
          console.error("Error loading employee data:", error);
        })
        .finally(() => {
          stopGlobalLoading('employee-data-load');
        });
    }
  }, [dispatch, startGlobalLoading, stopGlobalLoading, isActive]);

  // Load salary config groups
  useEffect(() => {
    getSalaryConfigGroups()
      .then((groups) => setSalaryConfigGroups(groups))
      .catch(() => {/* silently fail */});
  }, []);

  useEffect(() => {
    if (!isActive) return;

    let isCancelled = false;

    const loadEmployeeStatuses = async () => {
      try {
        const { default: axiosInstance } = await import("@/setup/axios/axiosInstance");

        const fetchStatusesByModule = async () => {
          const moduleCandidates = ["employees", "employee"];

          for (const moduleName of moduleCandidates) {
            try {
              const response = await axiosInstance.get(`/settings/statuses/by-module/?module=${moduleName}`);
              const data = response.data;
              const modulePayload = data?.[moduleName] || data?.employees || data?.employee || data;

              const normalized: EmployeeStatusOption[] = Array.isArray(modulePayload?.groups)
                ? modulePayload.groups.flatMap((group: any) => {
                    const groupStatus = group?.group_status || null;
                    const items = Array.isArray(group?.items) ? group.items : [];
                    return items.map((item: any) => ({
                      id: String(item.id),
                      name: item.name || "",
                      color: item.color || undefined,
                      is_default: !!item.is_default,
                      sort_order: item.sort_order ?? 0,
                      group: groupStatus
                        ? {
                            id: groupStatus.id,
                            name: groupStatus.name,
                            sort_order: groupStatus.sort_order ?? 999,
                          }
                        : undefined,
                    }));
                  })
                : Array.isArray(modulePayload?.items)
                  ? modulePayload.items.map((item: any) => ({
                      id: String(item.id),
                      name: item.name || "",
                      color: item.color || undefined,
                      is_default: !!item.is_default,
                      sort_order: item.sort_order ?? 0,
                      group: item?.group_status
                        ? {
                            id: item.group_status.id,
                            name: item.group_status.name,
                            sort_order: item.group_status.sort_order ?? 999,
                          }
                        : undefined,
                    }))
                  : Array.isArray(modulePayload)
                    ? modulePayload.map((item: any) => ({
                        id: String(item.id),
                        name: item.name || "",
                        color: item.color || undefined,
                        is_default: !!item.is_default,
                        sort_order: item.sort_order ?? 0,
                      }))
                    : [];

              if (normalized.length > 0) {
                return normalized;
              }
            } catch (error) {
              // Try next module candidate.
            }
          }

          return [] as EmployeeStatusOption[];
        };

        const apiStatuses = await fetchStatusesByModule();
        const fallbackStatuses: EmployeeStatusOption[] = [
          { id: "active", name: "Hoạt động", color: "#10b981", is_default: true, sort_order: 1 },
          { id: "inactive", name: "Không hoạt động", color: "#ef4444", is_default: false, sort_order: 2 },
          { id: "pending", name: "Chờ duyệt", color: "#f59e0b", is_default: false, sort_order: 3 },
        ];

        const sourceStatuses = apiStatuses.length > 0 ? apiStatuses : fallbackStatuses;
        const sortedStatuses = [...sourceStatuses].sort((a, b) => {
          const groupSortA = a.group?.sort_order ?? 999;
          const groupSortB = b.group?.sort_order ?? 999;
          if (groupSortA !== groupSortB) return groupSortA - groupSortB;

          const itemSortA = a.sort_order ?? 0;
          const itemSortB = b.sort_order ?? 0;
          if (itemSortA !== itemSortB) return itemSortA - itemSortB;

          return (a.name || "").localeCompare(b.name || "", "vi");
        });

        if (!isCancelled) {
          setEmployeeStatuses(sortedStatuses);

          const defaultStatus = sortedStatuses.find((status) => status.is_default) || sortedStatuses[0];
          if (defaultStatus && !isEditMode && !propInitialFormData?.status && !employee?.status) {
            setFormData((prev) => {
              const shouldApplyDefault = !prev.status || prev.status === "active";
              if (!shouldApplyDefault) {
                return prev;
              }

              return {
                ...prev,
                status: defaultStatus.id,
              };
            });
          }
        }
      } catch (error) {
        if (!isCancelled) {
          setEmployeeStatuses([
            { id: "active", name: "Hoạt động", color: "#10b981", is_default: true, sort_order: 1 },
            { id: "inactive", name: "Không hoạt động", color: "#ef4444", is_default: false, sort_order: 2 },
            { id: "pending", name: "Chờ duyệt", color: "#f59e0b", is_default: false, sort_order: 3 },
          ]);
        }
      }
    };

    loadEmployeeStatuses();

    return () => {
      isCancelled = true;
    };
  }, [employee?.status, isActive, isEditMode, propInitialFormData?.status]);

  // Handle backend validation errors
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  // Load employee data when in edit mode
  useEffect(() => {
    if (isEditMode && employee?.id) {
      // Only load if this is a different employee than the one we already loaded
      if (employeeLoadedRef.current === employee.id) {
        return;
      }

      // Wait for all required data to be loaded before mapping
      if (safeCompanies.length === 0) {
        // Don't reset employeeLoadedRef - just wait for data to load
        return;
      }

      // Store current formData roles before loading (in case employee was just created)
      if (formData.roles && Array.isArray(formData.roles) && formData.roles.length > 0) {
        rolesBeforeLoadRef.current = formData.roles.map((r: any) => String(r));
      }


      // First, try to extract roles from employee.user.roles (API may return roles in user object)
      let userRoles = (employee.user as any)?.roles || (employee as any)?.user?.roles;
      let normalizedRoles = Array.isArray(userRoles)
        ? userRoles.map((r: any) => String(r)).sort()
        : [];
      

      // Use employee data directly from props - it should already have all necessary data.
      // If employee has no linked user, prefer emp_email and do not fall back to created_by_email.
      const employeeAny = employee as any;
      const userPayload = employeeAny?.user && typeof employeeAny.user === 'object' ? employeeAny.user : null;
      const linkedUserId = String(userPayload?.id || employeeAny?.user_id || employeeAny?.userId || '').trim();
      const linkedUserEmail = String(
        userPayload?.email || employeeAny?.user_email || employeeAny?.linked_user_email || ''
      ).trim();
      const hasLinkedUser = !!(userPayload || linkedUserId || linkedUserEmail);
      const email = hasLinkedUser
        ? (linkedUserEmail || employee.user?.email || employeeAny?.user_email || employee.email || "")
        : (employeeAny?.emp_email || employee.email || "");
      
      const loadedFormData = {
        name: employee.name || "",
        nickname: employeeAny?.nickname || "",
        code: employee.code || "",
        status: resolveEmployeeStatusId(
          employee.status ?? employeeAny?.status_id,
          'active'
        ),
        job_title:
          (typeof employee.jobTitle === 'object'
            ? (employee.jobTitle as any)?.id
            : employee.jobTitle) ||
          employeeAny?.job_title_id ||
          employeeAny?.job_title ||
          "",
        area: typeof employee.area === 'object' ? (employee.area as any)?.id || "" : employee.area || "",
        office: employee.office || "",
        position: employee.position || "",
        company: typeof employee.company === 'object'
          ? (employee.company as any)?.id || (employee.company as any)?.company_id || ""
          : (employeeAny?.company_id || employee.company || ""),
        department: typeof employee.department === 'object'
          ? (employee.department as any)?.id || ""
          : (employeeAny?.department_id || employee.department || ""),
        date_of_birth: employeeAny?.date_of_birth || employeeAny?.dateOfBirth || "",
        gender: employeeAny?.gender || "",
        phone: employeeAny?.phone || "",
        start_date: employeeAny?.start_date || employeeAny?.startDate || "",
        resign_date: employeeAny?.resign_date || employeeAny?.resignDate || "",
        bank_account_name: employeeAny?.bank_account_name || employeeAny?.bankAccountName || "",
        bank_account_number: employeeAny?.bank_account_number || employeeAny?.bankAccountNumber || "",
        bank_name: employeeAny?.bank_name || employeeAny?.bankName || "",
        notes: employeeAny?.notes || "",
        relative_info: employeeAny?.relative_info || employeeAny?.relativeInfo || "",
        image1: employeeAny?.image1 || "",
        image2: employeeAny?.image2 || "",
        address: employee.address || { address_line: "", province: "", ward: "", free_address: "" },
        email: email,
        password: "",
        confirmPassword: "",
        fullName: employee.fullName || "",
        roles: normalizedRoles,
        attendance_type: employeeAny?.attendance_type || 'attendance',
        missions: normalizeMissions(employeeAny?.missions),
        dashboards: normalizeDashboards(employeeAny?.dashboards),
        salary_config_group: normalizeSalaryConfigGroupId(
          employeeAny?.salary_config_group || employeeAny?.primary_salary_config_group,
        ),
        salary_config_groups_secondary: normalizeSalaryConfigGroupIds(
          Array.isArray(employeeAny?.salary_config_groups_secondary)
            ? employeeAny.salary_config_groups_secondary
            : employeeAny?.secondary_salary_config_groups,
        ),
      };
      

      setFormData(loadedFormData);
      
      // Set userSelectionType based on whether employee has a linked user
      if (hasLinkedUser) {
        const linkedUser = userPayload || {};
        const resolvedFirstName = String(
          linkedUser.first_name || employeeAny?.user_first_name || linkedUser.name?.split(' ')[0] || linkedUser.full_name?.split(' ')[0] || ''
        ).trim();
        const resolvedLastName = String(
          linkedUser.last_name || employeeAny?.user_last_name || linkedUser.name?.split(' ').slice(1).join(' ') || linkedUser.full_name?.split(' ').slice(1).join(' ') || ''
        ).trim();
        const resolvedUsername = String(
          linkedUser.username || employeeAny?.user_username || employeeAny?.user_name || email?.split('@')[0] || ''
        ).trim();
        setUserSelectionType("existing");
        // Map employee.user to full UserModel format
        const userModel: UserModel = {
          id: String(linkedUser.id || linkedUserId || ''),
          email: String(linkedUser.email || linkedUserEmail || email || ''),
          username: resolvedUsername,
          first_name: resolvedFirstName,
          last_name: resolvedLastName,
          is_active: true,
          date_joined: "",
          role: "",
          role_display_name: "",
          status: "active",
          groups: [],
          updated_at: "",
        };
        setSelectedUser(userModel);

        // Fetch user roles from API if not already in employee data
        // But also check if we have roles in the current formData (from previous form state)
        // This handles the case where employee was just created and roles might not be in API yet
        const currentFormDataRoles = rolesBeforeLoadRef.current || [];
        const hasCurrentRoles = Array.isArray(currentFormDataRoles) && currentFormDataRoles.length > 0;
        // Also check roles stored before loading (for newly created employees)
        const storedRoles = rolesBeforeLoadRef.current || [];
        const hasStoredRoles = Array.isArray(storedRoles) && storedRoles.length > 0;
        
        const linkedUserRoleId = String(linkedUser.id || linkedUserId || '').trim();
        if (!normalizedRoles.length && linkedUserRoleId) {
          getUserRolesByUser(linkedUserRoleId)
            .then((userRolesResponse) => {
              // Extract role IDs from userRolesResponse
              // UserRole interface: { id: string, user: string, role: string, ... }
              // role is the role ID (string), not an object
              const fetchedRoleIds = Array.isArray(userRolesResponse)
                ? userRolesResponse
                    .map((ur: any) => {
                      // UserRole.role is the role ID (string), UserRole.id is the UserRole relationship ID
                      const roleId = ur.role || ur.role_id || ur.role?.id || ur.id;
                      return String(roleId);
                    })
                    .filter((id: string) => id && id !== 'undefined')
                : [];

              // Use fetched roles if available, otherwise fall back to stored roles or current formData roles
              // This handles the case where roles were just created but API hasn't returned them yet
              const finalRoles = fetchedRoleIds.length > 0
                ? fetchedRoleIds.sort()
                : (hasStoredRoles
                    ? storedRoles.map((r: any) => String(r)).sort()
                    : (hasCurrentRoles ? currentFormDataRoles.map((r: any) => String(r)).sort() : []));

              if (finalRoles.length > 0) {
                // Update formData with fetched roles - use functional update to ensure we get latest state
                setFormData((prev) => {
                  const updated = {
                    ...prev,
                    roles: [...finalRoles], // Create new array reference
                  };
                  return updated;
                });

                // Update initialFormData with fetched roles to keep them in sync
                setInitialFormData((prev: any) => {
                  if (prev) {
                    const updated = {
                      ...prev,
                      roles: [...finalRoles], // Create new array reference
                    };
                    return updated;
                  }
                  return prev;
                });
              }
            })
            .catch((error) => {
              // If fetch fails but we have roles in formData or stored, use those
              const fallbackRoles = hasStoredRoles
                ? storedRoles.map((r: any) => String(r)).sort()
                : (hasCurrentRoles ? currentFormDataRoles.map((r: any) => String(r)).sort() : []);

              if (fallbackRoles.length > 0) {
                setFormData((prev) => ({
                  ...prev,
                  roles: [...fallbackRoles],
                }));

                setInitialFormData((prev: any) => {
                  if (prev) {
                    return {
                      ...prev,
                      roles: [...fallbackRoles],
                    };
                  }
                  return prev;
                });
              }
            });
        } else if ((hasStoredRoles || hasCurrentRoles) && !normalizedRoles.length) {
          // If we have roles in stored or formData but not in employee data, use stored/formData roles
          const fallbackRoles = hasStoredRoles
            ? storedRoles.map((r: any) => String(r)).sort()
            : currentFormDataRoles.map((r: any) => String(r)).sort();

          setFormData((prev) => ({
            ...prev,
            roles: [...fallbackRoles],
          }));

          setInitialFormData((prev: any) => {
            if (prev) {
              return {
                ...prev,
                roles: [...fallbackRoles],
              };
            }
            return prev;
          });
        }
      } else {
        // If employee has no user, set to "no-user"
        setUserSelectionType("no-user");
        setSelectedUser(null);
      }

      // Set initialFormData to track changes (only once per employee)
      if (initialFormDataSetRef.current !== employee.id) {
        setInitialFormData(loadedFormData);
        initialFormDataSetRef.current = employee.id;
      }

      employeeLoadedRef.current = employee.id;
      setCreatedEmployee({ employee });
    }
  }, [employee, formData.roles, isEditMode, safeCompanies]);

  const handleImageAdded = (field: 'image1' | 'image2') => async (imageFile: any) => {
    if (!canEdit || !imageFile?.file) return;
    try {
      const result = await uploadEmployeeImage(imageFile.file as File, field);
      setFormData((prev) => ({
        ...prev,
        [field]: result.url,
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleImageRemoved = (field: 'image1' | 'image2') => () => {
    if (!canEdit) return;
    setFormData((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  const handleInputChange = (field: string, value: string | any) => {
    // Handle address object separately
    if (field === "address") {
      setFormData((prev) => ({
        ...prev,
        address: value,
      }));
      return;
    }

    // Ensure value is never null for Select components
    const safeValue = value || "";

    setFormData((prev) => {
      let newFormData = {
        ...prev,
        [field]: safeValue,
      };

      // Reset department when company changes
      if (field === "company") {
        newFormData = {
          ...newFormData,
          department: "",
        };
      }

      return newFormData;
    });

    if (
      userSelectionType === "new" &&
      (field === "fullName" || field === "email" || field === "nickname")
    ) {
      newUserDraftRef.current = {
        ...newUserDraftRef.current,
        [field]: safeValue,
      };
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleUserSelectionTypeChange = (type: UserSelectionType) => {
    if (userSelectionType === "new" && type !== "new") {
      newUserDraftRef.current = {
        fullName: formData.fullName || "",
        email: formData.email || "",
        nickname: formData.nickname || "",
      };
    }

    setUserSelectionType(type);
    setSelectedUser(null);
    setErrors((prev) => ({
      ...prev,
      user: "",
      password: "",
      confirmPassword: "",
    }));

    if (type === "new") {
      setFormData((prev) => ({
        ...prev,
        email: newUserDraftRef.current.email || "",
        password: "",
        confirmPassword: "",
        fullName: newUserDraftRef.current.fullName || "",
        name: newUserDraftRef.current.fullName || prev.name || "",
        nickname: newUserDraftRef.current.nickname || prev.nickname || "",
      }));
    } else if (type === "no-user") {
      setFormData((prev) => ({
        ...prev,
        password: "",
        confirmPassword: "",
      }));
    }
  };

  const handleUserSelect = (user: UserModel | null) => {
    setSelectedUser(user);

    if (user) {
      // Fill form data with selected user information
      const fullName = `${user.first_name || ""} ${user.last_name || ""
        }`.trim();

      setFormData((prev) => {
        const newFormData = {
          ...prev,
          email: user.email || "",
          fullName: fullName,
          name: fullName, // Also set name field
          // Don't fill password fields for existing user
          password: "",
          confirmPassword: "",
        };
        return newFormData;
      });

      // Fetch roles for the selected user
      if (user.id) {
        getUserRolesByUser(user.id)
          .then((userRolesResponse) => {
            // Extract role IDs from userRolesResponse
            const fetchedRoleIds = Array.isArray(userRolesResponse)
              ? userRolesResponse.map((ur: any) => {
                  const roleId = ur.role || ur.role_id || ur.role?.id || ur.id;
                  return String(roleId);
                }).filter((id: string) => id && id !== 'undefined')
              : [];
            
            const normalizedRoles = fetchedRoleIds.sort();
            
            // Update formData with fetched roles
            if (normalizedRoles.length > 0) {
              setFormData((prev) => ({
                ...prev,
                roles: [...normalizedRoles],
              }));
            }
          })
          .catch((error) => {
            // Silently fail - roles might not be available
          });
      }
    } else {
      // Clear roles when user is deselected
      setFormData((prev) => ({
        ...prev,
        roles: [],
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const preferredCompanyId = resolvePreferredCompanyId();
    const effectiveCompanyId = String(formData.company || preferredCompanyId || '').trim();

    if (!formData.fullName.trim()) {
      newErrors.name = "Tên nhân viên là bắt buộc";
    }

    const requiresEmail = userSelectionType !== "no-user";
    if (requiresEmail && !formData.email.trim()) {
      newErrors.email = "Email là bắt buộc";
    } else if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email không hợp lệ";
    }

    // Validate password only for new users
    if (userSelectionType === "new") {
      if (!formData.password) {
        newErrors.password = "Mật khẩu là bắt buộc";
      } else if (formData.password.length < 6) {
        newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Mật khẩu xác nhận không khớp";
      }
    }

    // Validate user selection for existing users
    if (userSelectionType === "existing" && !selectedUser) {
      newErrors.user = "Vui lòng chọn user đã tồn tại";
    }

    if (!effectiveCompanyId) {
      newErrors.company = "Công ty là bắt buộc";
    }

    if (!formData.company && effectiveCompanyId) {
      setFormData((prev) => ({
        ...prev,
        company: effectiveCompanyId,
      }));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    
    // Clear previous messages
    setSuccessMessage(null);
    setErrorMessage(null);

    // Check permissions before proceeding
    if (!canEdit) {
      setErrorMessage(isEditMode ? "Bạn không có quyền cập nhật nhân viên" : "Bạn không có quyền tạo nhân viên mới");
      return;
    }

    const isValid = validateForm();

    if (isValid) {
      let submitData: any;
      const preferredCompanyId = resolvePreferredCompanyId();
      const effectiveCompanyId = String(formData.company || preferredCompanyId || '').trim();

      if (isEditMode) {
        // For update, send employee fields and user fields if changed
        submitData = {
          // Employee fields
          name: formData.fullName,
          nickname: formData.nickname || "",
          code: formData.code,
          status: resolveEmployeeStatusId(formData.status),
          attendance_type: formData.attendance_type || 'attendance',
          missions: normalizeMissions(formData.missions),
          dashboards: normalizeDashboards(formData.dashboards),
          job_title_id: formData.job_title,
          area_id: formData.area,
          company_id: effectiveCompanyId,
          department_id: formData.department,
        } as any;

        // Always add address_obj if formData.address exists
        if (formData.address) {
          submitData.address_obj = formData.address;
        }

        // Only add explicit user fields if they have values
        // Note: first_name and last_name will be auto-parsed from employee name by backend
        if (userSelectionType === "no-user") {
          submitData.userSelectionType = "no-user";
          if (formData.email && formData.email.trim()) {
            submitData.emp_email = formData.email.trim();
          }
        } else if (formData.email && formData.email.trim()) {
          submitData.email = formData.email;
        }

        // Add roles if they exist and have changed
        if (formData.roles && Array.isArray(formData.roles) && formData.roles.length > 0) {
          submitData.roles = formData.roles;
        }

      } else {
        // For create, send all fields including user selection
        const { address, office, position, password, confirmPassword, roles, ...restFormData } = formData;
        const shouldAttachUserFields = userSelectionType !== "no-user";
        submitData = {
          ...restFormData,
          name: formData.fullName,
          company: effectiveCompanyId,
          address_obj: address, // Rename address to address_obj
          userSelectionType,
          selectedUserId:
            userSelectionType === "existing" ? selectedUser?.id : null,
        };

        if (shouldAttachUserFields) {
          submitData.password = password;
          submitData.confirmPassword = confirmPassword;
          submitData.roles = roles;
        } else {
          submitData.roles = [];
          if (formData.email && formData.email.trim()) {
            submitData.emp_email = formData.email.trim();
          }
        }
      }

      if (submitData) {
        submitData.nickname = formData.nickname || "";
        submitData.date_of_birth = formData.date_of_birth || null;
        submitData.gender = formData.gender || "";
        submitData.phone = formData.phone || "";
        submitData.start_date = formData.start_date || null;
        submitData.resign_date = formData.resign_date || null;
        submitData.bank_account_name = formData.bank_account_name || "";
        submitData.bank_account_number = formData.bank_account_number || "";
        submitData.bank_name = formData.bank_name || "";
        submitData.notes = formData.notes || "";
        submitData.relative_info = formData.relative_info || "";
        submitData.image1 = formData.image1 || "";
        submitData.image2 = formData.image2 || "";
        submitData.primary_salary_config_group = formData.salary_config_group || null;
        submitData.secondary_salary_config_groups = Array.isArray(formData.salary_config_groups_secondary)
          ? formData.salary_config_groups_secondary
          : [];
      }

      try {
        
        setIsSubmitting(true);
        startGlobalLoading(isEditMode ? 'Đang cập nhật nhân viên...' : 'Đang tạo nhân viên...', 'employee-form-save');
        if (isEditMode && onUpdate) {
          
          // Nếu có roles thay đổi và có user ID, update user roles trước
          if (formData.roles && Array.isArray(formData.roles) && employee?.user?.id) {
            const currentRoles = formData.roles.map((r: any) => String(r)).sort();
            const initialRoles = initialFormData?.roles 
              ? initialFormData.roles.map((r: any) => String(r)).sort()
              : [];
            
            const rolesChanged = JSON.stringify(currentRoles) !== JSON.stringify(initialRoles);
            
            if (rolesChanged) {
              
              try {
                // Update user roles trước
                await updateUser(employee.user.id, {
                  company: getSelectedCompany()?.company_id || '',
                  roles: formData.roles
                });
                
                
                // Fetch lại user roles để đảm bảo có đúng data
                const updatedUserRoles = await getUserRolesByUser(employee.user.id);
                const updatedRoleIds = Array.isArray(updatedUserRoles)
                  ? updatedUserRoles.map((ur: any) => String(ur.role || ur.role_id || ur.role?.id || ur.id))
                      .filter((id: string) => id && id !== 'undefined')
                      .sort()
                  : formData.roles.map((r: any) => String(r)).sort();
                
                
                // Update formData với roles đã được update
                setFormData((prev) => ({
                  ...prev,
                  roles: updatedRoleIds,
                }));
                
                // Update initialFormData với roles đã được update
                setInitialFormData((prev: any) => {
                  if (prev) {
                    return {
                      ...prev,
                      roles: [...updatedRoleIds],
                    };
                  }
                  return prev;
                });
              } catch (userRolesError: any) {
                // Continue with employee update even if user roles update fails
                // Backend might handle roles update automatically
              }
            }
          }
          
          // Update existing employee
          
          const response = await onUpdate(
            submitData,
            createdEmployee?.employee?.id
          );
          
          
          // Fetch lại user roles từ API sau khi update employee để đảm bảo có đúng roles
          let finalRoles = formData.roles ? [...formData.roles].map((r: any) => String(r)).sort() : [];
          if (employee?.user?.id) {
            try {
              const userRolesResponse = await getUserRolesByUser(employee.user.id);
              const fetchedRoleIds = Array.isArray(userRolesResponse)
                ? userRolesResponse.map((ur: any) => String(ur.role || ur.role_id || ur.role?.id || ur.id))
                    .filter((id: string) => id && id !== 'undefined')
                    .sort()
                : [];
              
              if (fetchedRoleIds.length > 0) {
                finalRoles = fetchedRoleIds;
              }
            } catch (error) {
            }
          }
          
          // Update initialFormData với response data để button bị disable sau khi update thành công
          if (response && initialFormData) {
            
          const updatedInitialFormData = {
              ...initialFormData,
              name: response.name || formData.fullName,
              nickname: response.nickname || formData.nickname || "",
              code: response.code || formData.code,
              status: resolveEmployeeStatusId(
                response.status ?? formData.status,
                resolveEmployeeStatusId(formData.status)
              ),
              job_title: response.job_title_id || formData.job_title,
              area: response.area_id || formData.area,
              company: response.company_id || formData.company,
              department: response.department_id || formData.department,
              date_of_birth: response.date_of_birth ?? formData.date_of_birth,
              gender: response.gender ?? formData.gender,
              phone: response.phone ?? formData.phone,
              start_date: response.start_date ?? formData.start_date,
              resign_date: response.resign_date ?? formData.resign_date,
              bank_account_name: response.bank_account_name ?? formData.bank_account_name,
              bank_account_number: response.bank_account_number ?? formData.bank_account_number,
              bank_name: response.bank_name ?? formData.bank_name,
              notes: response.notes ?? formData.notes,
              relative_info: response.relative_info ?? formData.relative_info,
              image1: response.image1 ?? formData.image1,
              image2: response.image2 ?? formData.image2,
                attendance_type: response.attendance_type ?? formData.attendance_type,
              missions: normalizeMissions(response.missions ?? formData.missions),
              dashboards: normalizeDashboards(response.dashboards ?? formData.dashboards),
              email: response.email || formData.email,
              fullName: response.name || formData.fullName,
              address: response.address || formData.address,
              // Sử dụng finalRoles đã được fetch từ API - tạo new array reference
              roles: [...finalRoles], // Deep copy để đảm bảo reference mới
            };
            
            
            setInitialFormData(updatedInitialFormData);
            
            // Đồng bộ formData với initialFormData để đảm bảo consistency
            setFormData((prev) => {
              const updated = {
                ...prev,
                roles: [...finalRoles], // Deep copy để đảm bảo reference mới
              };
              return updated;
            });
            
          }
          
          setSuccessMessage("Nhân viên đã được cập nhật thành công!");
        } else {
          // Create new employee
          await onSave(submitData);

          setSuccessMessage("Nhân viên đã được tạo thành công!");

          // Tab will be updated by parent component (EmployeesModule)
          // No need to manually switch to edit mode - parent handles tab update
        }
      } catch (error: any) {
        
        // Use formatErrorForSnackbar helper to display error messages
        const errorMessage = formatErrorForSnackbar(error) || "Có lỗi xảy ra khi tạo/cập nhật nhân viên";
        setErrorMessage(errorMessage);
      } finally {
        setIsSubmitting(false);
        stopGlobalLoading('employee-form-save');
      }
    } else {
      // Show specific validation errors
      const errorMessages = Object.values(errors).filter(error => error.trim() !== '');
      if (errorMessages.length > 0) {
        setErrorMessage(`Lỗi validation: ${errorMessages.join(', ')}`);
      } else {
        setErrorMessage("Vui lòng kiểm tra lại thông tin đã nhập");
      }
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <>
      <AppForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        submitting={isSubmitting}
        submitDisabled={isSubmitting || !hasFormDataChanged || !canEdit}
        submitLabel={isEditMode ? 'Cập nhật' : 'Lưu'}
        submittingLabel={isEditMode ? 'Đang cập nhật...' : 'Đang tạo...'}
      >
        <BasicInfo
          formData={formData}
          errors={errors}
          canEdit={canEdit}
          statusOptions={employeeStatuses}
          onChange={handleInputChange}
        />
         <JobPositionSection
          formData={formData}
          errors={errors}
          hasGlobalRole={hasGlobalRole}
          canEdit={canEdit}
          safeCompanies={scopedJobPositionCompanies}
          filteredDepartments={filteredDepartments}
          onChange={handleInputChange}
        />

        <AdditionalInfoSection
          formData={formData}
          canEdit={canEdit}
          onChange={handleInputChange}
          onImageAdded={handleImageAdded}
          onImageRemoved={handleImageRemoved}
        />

        

       

        <AccountSalary
          salaryConfigGroups={salaryConfigGroups}
          salaryConfigGroup={formData.salary_config_group}
          salaryConfigGroupsSecondary={formData.salary_config_groups_secondary || []}
          bankAccountName={formData.bank_account_name}
          bankAccountNumber={formData.bank_account_number}
          bankName={formData.bank_name}
          canEdit={canEdit}
          onChange={handleInputChange}
        />

        <AccountInfo
          userSelectionType={userSelectionType}
          isEditMode={isEditMode}
          canEdit={canEdit}
          errors={errors}
          selectedUser={selectedUser}
          availableUsersEnabled={isActive && userSelectionType === 'existing'}
          fetchAvailableUsers={fetchAvailableUsers}
          roles={formData.roles || []}
          password={formData.password}
          confirmPassword={formData.confirmPassword}
          email={formData.email}
          roleSelectorCompanyIds={hasGlobalRole ? undefined : roleSelectorCompanyIds}
          roleSelectorGroupByCompany={hasGlobalRole}
          getUserDisplayName={getUserDisplayName}
          onUserSelectionTypeChange={handleUserSelectionTypeChange}
          onUserSelect={handleUserSelect}
          onFieldChange={handleInputChange}
        />

      </AppForm>

       


      {/* Success Snackbar */}
      <MessageSnackbar
        open={!!(propSuccessMessage || successMessage)}
        message={propSuccessMessage || successMessage}
        severity="success"
        onClose={() => {
          setSuccessMessage(null);
          if (onClearSuccess) {
            onClearSuccess();
          }
        }}
      />

      {/* Error Snackbar */}
      <MessageSnackbar
        open={!!(errorMessage || error)}
        message={errorMessage || error}
        severity="error"
        onClose={() => {
          setErrorMessage(null);
          if (onClearError) {
            onClearError();
          }
        }}
      />
    </>
  );
});

export default NewEmployee;
